// Lib
import axios from 'axios';

// Hooks
import { useEffect, useState } from 'react';
import { ErrorCategory, handleError } from '@services/errorService';

interface Country {
  name: string;
  iso2: string;
}

interface State {
  name: string;
  state_code: string;
  code?: string;
}

interface City {
  name: string;
}

interface GeoApiResponse<T> {
  items: T[];
  total: number;
}

interface Answers {
  country?: string;
  state?: string;
}

interface UseGeoDataReturn {
  countries: Country[];
  states: State[];
  cities: City[];
}

export const useGeoData = (answers: Answers): UseGeoDataReturn => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const allCountries: Country[] = [];
        const limit = 20;
        let skip = 0;
        let total = 0;

        const firstResponse = await axios.get<GeoApiResponse<Country>>(
          `https://geo.mrbl.bio/countries?limit=${limit}&skip=${skip}`,
        );
        const firstData = firstResponse.data;

        allCountries.push(...firstData.items);
        total = firstData.total;
        skip += limit;

        while (skip < total) {
          const response = await fetch(
            `https://geo.mrbl.bio/countries?limit=${limit}&skip=${skip}`,
          );
          const data: GeoApiResponse<Country> = await response.json();
          allCountries.push(...data.items);
          skip += limit;
        }

        setCountries(allCountries);
      } catch (error) {
        handleError(error, { category: ErrorCategory.API, silent: true });
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      if (!answers?.country) {
        setStates([]);
        return;
      }

      try {
        const selectedCountry = countries.find(
          (c) => c.name === answers?.country,
        );
        if (!selectedCountry) return;

        const allStates: State[] = [];
        const limit = 20;
        let skip = 0;
        let total = 0;

        const firstResponse = await axios.get<GeoApiResponse<State>>(
          `https://geo.mrbl.bio/states?country_iso2=${selectedCountry.iso2}&limit=${limit}&skip=${skip}`,
        );
        const firstData = firstResponse.data;

        allStates.push(...firstData.items);
        total = firstData.total;
        skip += limit;

        while (skip < total) {
          const response = await axios.get<GeoApiResponse<State>>(
            `https://geo.mrbl.bio/states?country_iso2=${selectedCountry.iso2}&limit=${limit}&skip=${skip}`,
          );
          const data = response.data;
          allStates.push(...data.items);
          skip += limit;
        }

        setStates(allStates);
      } catch (error) {
        handleError(error, { category: ErrorCategory.API, silent: true });
        setStates([]);
      }
    };

    fetchStates();
  }, [answers?.country, countries]);

  useEffect(() => {
    const fetchCities = async () => {
      if (!answers?.state || !answers?.country) {
        setCities([]);
        return;
      }

      try {
        const selectedCountry = countries.find(
          (c) => c.name === answers?.country,
        );
        const selectedState = states.find((s) => s.name === answers?.state);

        if (!selectedCountry || !selectedState) return;

        const allCities: City[] = [];
        const limit = 20;
        let skip = 0;
        let total = 0;

        const firstResponse = await axios.get<GeoApiResponse<City>>(
          `https://geo.mrbl.bio/cities?country_iso2=${selectedCountry.iso2}&state_code=${selectedState.state_code}&limit=${limit}&skip=${skip}`,
        );
        const firstData = firstResponse.data;

        allCities.push(...firstData.items);
        total = firstData.total;
        skip += limit;

        while (skip < total) {
          const response = await axios.get<GeoApiResponse<City>>(
            `https://geo.mrbl.bio/cities?country_iso2=${selectedCountry.iso2}&state_code=${selectedState.code}&limit=${limit}&skip=${skip}`,
          );
          const data = response.data;
          allCities.push(...data.items);
          skip += limit;
        }

        setCities(allCities);
      } catch (error) {
        handleError(error, { category: ErrorCategory.API, silent: true });
        setCities([]);
      }
    };

    fetchCities();
  }, [answers?.state, answers?.country, countries, states]);

  return { countries, states, cities };
};
